import { Show } from '@library/components'
import { DetailedError } from '@library/utils'
import type { ErrorInfo, PropsWithChildren } from 'react'
import { Component } from 'react'

import css from './ErrorBoundary.css'
import { Forbidden } from './Forbidden'
import { NotFound } from './NotFound'
import { SomeError } from './SomeError'
import type { ErrorComponent } from './types'

type ErrorBoundaryProps = PropsWithChildren<{
  ErrorComponent?: ErrorComponent
}>

interface State<ErrorType extends Error> {
  hasError: boolean
  error?: ErrorType
}

export class ErrorBoundary<T extends Error = Error> extends Component<
  ErrorBoundaryProps,
  State<T>
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    // Обновить состояние с тем, чтобы следующий рендер показал запасной UI.
    return { hasError: true }
  }

  componentDidCatch(error: T, errorInfo: ErrorInfo) {
    // Можно также сохранить информацию об ошибке в соответствующую службу журнала ошибок
    console.error('Uncaught error:', error, errorInfo)
    this.setState(prev => ({ ...prev, error }))
  }

  clearErrorState = () => {
    this.setState(prev => ({ ...prev, hasError: false }))
  }

  renderError() {
    const error = this.state.error

    if (error instanceof DetailedError) {
      switch (error.code) {
        case 403:
          return <Forbidden onSelectAction={this.clearErrorState} />
        case 404:
          return <NotFound onSelectAction={this.clearErrorState} />
      }
    }

    return (
      <Show
        when={this.props.ErrorComponent}
        fallback={<SomeError onSelectAction={this.clearErrorState} />}>
        {ErrorComponent => {
          return <ErrorComponent onSelectAction={this.clearErrorState} />
        }}
      </Show>
    )
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return <div className={css.container}>{this.renderError()}</div>
  }
}
